// src/oura/merchant/drawers/ReportDrawer.tsx
//
// One report: its date and length, the one line that sums it up, what is
// inside when the server says, the PDF itself, and the two ways on from
// there -- full screen in the platform's reader, or download it.

import { useId, useState } from "react";
import { createPortal } from "react-dom";
import { BookOpenIcon, DownloadIcon, FileTextIcon, XIcon } from "lucide-react";
import { isInAppSnapshot, type ClientReportLink } from "@service/aeo/clientReview";
import { SnapshotView } from "../../components/onboarding/BaselineSnapshot";
import { useBaselineSnapshot } from "../../components/onboarding/useBaselineSnapshot";
import { PDFViewer } from "@component/pdf-viewer";
import { Drawer, DrawerBlock, DrawerFoot, DrawerHead, PillButton, PillLink, TYPE, useEscapeLayer } from "@oura/ui";
import { cn } from "../../lib/cn";
import { MERCHANT_V2 } from "../../merchantV2";
import { shortDateOrNull } from "../../lib/oura";

/**
 * "16 Sep 2026 · PDF · 6 pages": the line under a report's title, on its row
 * and in its drawer. The page count only when the server counted them; with
 * no file, why not, in words that make no claim about email.
 */
export function ReportMeta({ report }: { report: ClientReportLink }) {
    return <>{reportMetaText(report)}</>;
}

/** The same line, as text: the drawer's status line. */
function reportMetaText(report: ClientReportLink): string {
    const file = report.url ? "PDF" : isInAppSnapshot(report) ? "Opens here" : report.urlAbsent === "not_uploaded" ? "Not uploaded yet" : "No file yet";
    const pages = report.url && typeof report.pages === "number" && report.pages > 0 ? `${report.pages} ${report.pages === 1 ? "page" : "pages"}` : null;
    return [shortDateOrNull(report.date), file, pages].filter(Boolean).join(" · ");
}

/** A Google Drive file page, which the PDF viewer cannot read: it opens in Drive. */
function inDrive(url: string): boolean {
    return /^https:\/\/(drive|docs)\.google\.com\//.test(url);
}

export function ReportDrawer({ report, onClose }: { report: ClientReportLink | null; onClose: () => void }) {
    const [reading, setReading] = useState(false);
    const readId = useId();
    if (!report) return null;
    // The viewer sits over the drawer in a portal of its own; closing it puts
    // focus back on "Read it here", which is still there under it.
    const closeViewer = () => {
        setReading(false);
        document.getElementById(readId)?.focus();
    };
    const sections = report.sections?.filter((s) => s.trim()) ?? [];

    // The Baseline Snapshot has no file: it is drawn here, the same as staff see it (#4457).
    if (isInAppSnapshot(report)) return <SnapshotDrawer report={report} assessmentId={report.assessmentId!} onClose={onClose} />;

    // ONE STEP, NOT TWO (#4062 MR-FE-8). "Read" used to open a drawer whose
    // job was to say the summary and offer another button; the report itself
    // was a press further in. A report with a file now opens straight into
    // the panel, with the summary and what is inside above it. A Drive file
    // keeps the drawer, because the viewer here cannot read a Drive page and
    // the only thing to offer is the link out.
    if (MERCHANT_V2 && report.url && !inDrive(report.url)) {
        return <ReportViewer report={report} url={report.url} onClose={onClose} summary={report.summary} sections={sections} />;
    }

    return (
        <>
            <Drawer
                open
                onClose={onClose}
                // The SAME width the question drawer uses (#4025 FE-7), passed
                // through the same prop, so Oura has one wide drawer and not a
                // third size. A PDF page does not fit the 600px default at any
                // readable zoom (#4026 C-FE-8).
                width={800}
            >
                <DrawerHead
                    lead={<FileTextIcon aria-hidden size={14} strokeWidth={2} className="text-[#8E8E93]" />}
                    title={report.title}
                    status={{ label: reportMetaText(report) }}
                />
                {report.summary && <DrawerBlock label="In 1 line">{report.summary}</DrawerBlock>}
                {sections.length > 0 && (
                    <DrawerBlock label="Inside">
                        <ol className="list-plain m-0 mt-1 p-0">
                            {sections.map((section, i) => (
                                <li key={section} className="flex min-h-10 items-center gap-3 border-t border-gray-100 first:border-t-0">
                                    <span className={cn(TYPE.tm, "w-5 tabular-nums text-gray-400")}>{i + 1}</span>
                                    {section}
                                </li>
                            ))}
                        </ol>
                    </DrawerBlock>
                )}
                {/* THE PDF ITSELF, with no further click (#4026 C-FE-8). An
                    uploaded report has no `sections`, so its drawer held a
                    title, a date and a button -- a reader had to ask for the
                    thing they had come for. It sits under "In 1 line" and
                    "Inside", because those say what the file is about and they
                    are short.
    
                    `PDFViewer` renders ONE page at a time and brings its own
                    page and zoom controls, so this is the first page on open
                    with a way through the rest, not a whole report rendered
                    into a drawer. The full-screen reader stays in the foot for
                    anything that wants the width. */}
                {report.url && !inDrive(report.url) && (
                    <DrawerBlock label="The report">
                        <div className={cn("mt-1 overflow-hidden rounded-xl ring-1 ring-inset ring-gray-200", MERCHANT_V2 ? "bg-white" : "bg-gray-100")}>
                            <PDFViewer url={report.url} className="mx-auto" defaultZoom={0.85} />
                        </div>
                    </DrawerBlock>
                )}
                {!report.url && (
                    <p className={cn(TYPE.tc, "m-0 px-1 text-gray-500")}>There is no file to open here yet.</p>
                )}
                {report.url && inDrive(report.url) && (
                    // A filed copy in Google Drive: the viewer here cannot load a Drive page, so it opens there.
                    <DrawerFoot
                        first={
                            <PillLink variant="primary" icon={BookOpenIcon} href={report.url} target="_blank" rel="noopener noreferrer">
                                Read it here
                                <span className="sr-only">, opens the copy in Google Drive in a new tab</span>
                            </PillLink>
                        }
                    />
                )}
                {report.url && !inDrive(report.url) && (
                    <DrawerFoot
                        first={
                            <PillButton id={readId} variant="primary" icon={BookOpenIcon} onClick={() => setReading(true)}>
                                Open full screen
                            </PillButton>
                        }
                        second={
                            <PillLink variant="secondary" icon={DownloadIcon} href={report.url} download={`${report.title}.pdf`}>
                                Download PDF
                            </PillLink>
                        }
                    />
                )}
            </Drawer>
            {reading && report.url && !inDrive(report.url) && <ReportViewer report={report} url={report.url} onClose={closeViewer} />}
        </>
    );
}

/**
 * The platform's PDF viewer (the one Recruiter uses for resumes), in a
 * full-height panel over the drawer. It joins the escape stack, so Escape
 * closes the viewer first and the drawer on the next press.
 */
function ReportViewer({
    report,
    url,
    onClose,
    summary,
    sections,
}: {
    report: ClientReportLink;
    url: string;
    onClose: () => void;
    /** Shown above the file when the panel is the only step (#4062 MR-FE-8). */
    summary?: string | null;
    sections?: string[];
}) {
    useEscapeLayer(onClose, true);
    return createPortal(
        <div role="dialog" aria-modal="true" aria-label={report.title} className="fixed inset-0 z-50">
            <div aria-hidden className="absolute inset-0 bg-gray-900/40" onClick={onClose} />
            {/* 960px: an A4 page reads at full size, with a strip of the
                page behind it still showing so it reads as a panel. Full
                screen on a phone, as before. */}
            <div className={cn("absolute inset-y-0 right-0 flex w-full flex-col border-l border-gray-200 bg-white shadow-2xl", MERCHANT_V2 ? "max-w-[960px]" : "max-w-4xl")}>
                <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-5 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50">
                            <FileTextIcon className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                            <p className="m-0 truncate text-sm font-semibold text-gray-900">{report.title}</p>
                            <p className="m-0 text-xs tabular-nums text-gray-500">{shortDateOrNull(report.date)}</p>
                        </div>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-2">
                        {MERCHANT_V2 ? (
                            <PillLink variant="secondary" icon={DownloadIcon} href={url} download={`${report.title}.pdf`}>
                                Download
                            </PillLink>
                        ) : (
                            <a
                                href={url}
                                download={`${report.title}.pdf`}
                                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                            >
                                <DownloadIcon className="h-3.5 w-3.5" />
                                Download
                            </a>
                        )}
                        <button
                            type="button"
                            autoFocus
                            onClick={onClose}
                            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            aria-label="Close"
                        >
                            <XIcon className="h-4 w-4" />
                        </button>
                    </div>
                </div>
                <div className={cn("flex-1 overflow-auto p-4", MERCHANT_V2 ? "bg-white" : "bg-gray-100")}>
                    {(summary || (sections && sections.length > 0)) && (
                        <div className="mx-auto mb-4 max-w-full rounded-xl bg-white p-4 shadow-sm">
                            {summary && (
                                <>
                                    <p className={cn(TYPE.lab, "m-0")}>Summary</p>
                                    <p className={cn(TYPE.tb, "m-0 mt-0.5 text-gray-700")}>{summary}</p>
                                </>
                            )}
                            {sections && sections.length > 0 && (
                                <>
                                    <p className={cn(TYPE.lab, "m-0", summary ? "mt-3" : undefined)}>Inside</p>
                                    <ol className="list-plain m-0 mt-1 p-0">
                                        {sections.map((section, i) => (
                                            <li key={section} className="flex min-h-9 items-center gap-3 border-t border-gray-100 first:border-t-0">
                                                <span className={cn(TYPE.tm, "w-5 tabular-nums text-gray-400")}>{i + 1}</span>
                                                {section}
                                            </li>
                                        ))}
                                    </ol>
                                </>
                            )}
                        </div>
                    )}
                    <PDFViewer url={url} className="mx-auto" defaultZoom={1.1} />
                </div>
            </div>
        </div>,
        document.body,
    );
}

/**
 * The Baseline Snapshot, drawn from its scan with the staff drawer's own view
 * and width (#4457). Read-only: a client cannot make one, so there is no
 * "Make it now", only a way to read again when the read fails.
 */
function SnapshotDrawer({ report, assessmentId, onClose }: { report: ClientReportLink; assessmentId: string; onClose: () => void }) {
    const { load, read } = useBaselineSnapshot(assessmentId);
    const snap = load.state === "read" ? load.read : null;
    return (
        <Drawer open onClose={onClose} width={1040}>
            <DrawerHead
                lead={<FileTextIcon aria-hidden size={14} strokeWidth={2} className="text-[#8E8E93]" />}
                title={report.title}
                status={{ label: reportMetaText(report) }}
            />
            {load.state === "loading" ? (
                <p className={cn(TYPE.tc, "m-0 px-1 text-gray-500")}>Loading your Baseline Snapshot…</p>
            ) : load.state === "failed" ? (
                <div className="flex flex-wrap items-center gap-3 px-1">
                    <p role="alert" className={cn(TYPE.tc, "m-0 text-rose-600")}>Your Baseline Snapshot could not be read.</p>
                    <PillButton size="sm" onClick={() => void read()}>Try again</PillButton>
                </div>
            ) : snap?.status === "ready" && snap.data ? (
                <SnapshotView snap={snap.data} />
            ) : (
                <p className={cn(TYPE.tc, "m-0 px-1 text-gray-500")}>Your Baseline Snapshot is not ready yet. It shows here as soon as it is.</p>
            )}
        </Drawer>
    );
}
